import { Injectable } from "@angular/core";
import { ApiService } from "../../core/API/api.service";
import { finalize, Observable, Subject } from "rxjs";
import {
    AgentModel,
    FileUploadProfile,
    CountrySelect,
    NoResidenceLicenseModel,
    Languages,
    StatesSelect,
    DocumentAgentModel,
} from "../model/agent.model";
import { ResponseModel } from "../model/response.model";
import { pageableModel, ParametersModel } from "../model/pageable.model";
import { uploadFile, uploadPercentage, uploadSnapshots, uploadThenUrl, deleteObjectAt, downloadUrl } from "../firebase/storage.util";
import { DocumentTypeModel } from "../model/document-type.model";

@Injectable({
    providedIn: "root",
})
export class AgentService {
    private basePath = "/agents_profile";
    private downloadURL: string;
    private sessionData: any = {};
    private sessionDataProfile: any = {};

    private _listners = new Subject<any>();

    constructor(private _apiService: ApiService) {
        this.sessionData = JSON.parse(localStorage.getItem("dataAgent") || "{}");
        this.sessionDataProfile = JSON.parse(localStorage.getItem("data") || "{}");
    }

    listen(): Observable<any> {
        return this._listners.asObservable();
    }

    getAgentsList(params: ParametersModel): Observable<ResponseModel<pageableModel<AgentModel[]>>> {
        return this._apiService.get(`/user/agent/list`, {
            start: params.start,
            length: params.length,
            order: params.order,
            sort: params.sort,
            search: typeof params.search === "string" ? params.search : "",
        });
    }

    getOne(id: number): Observable<ResponseModel<AgentModel>> {
        let data = { id: id };
        return this._apiService.get(`/user`, data);
    }

    delete(id: number): Observable<ResponseModel<AgentModel>> {
        let data = { id, status };
        return this._apiService.put(`/user/delete`, data);
    }

    changeStatus(id: number, status: number): Observable<ResponseModel<AgentModel>> {
        let data = { id, status };
        return this._apiService.put(`/user/change/status`, data);
    }

    updateHeaderAgentData(data: any, profile: any) {
        //para actualizar en tiempo real los cambios en el header del profile
        this._listners.next(data);
        this._listners.next(profile);
    }
    sendWelcomeMail(id: number): Observable<ResponseModel<AgentModel>> {
        let data = { id_user: id };
        return this._apiService.post("/user/sendwelcomeletter", data);
    }

    pushFileToStorage(fileUpload: FileUploadProfile, id: number): Observable<number | undefined> {
        const filePath = `${this.basePath}/${this.sessionDataProfile.idClient}/${id}/${fileUpload.file.name}`;
        const uploadTask = uploadFile(filePath, fileUpload.file);

        uploadSnapshots(uploadTask)
            .pipe(
                finalize(() => {
                    downloadUrl(filePath).subscribe((downloadURL) => {
                        fileUpload.path = filePath;
                        fileUpload.url = downloadURL;
                        fileUpload.name = fileUpload.file.name;
                        this.downloadURL = downloadURL;
                        this.updateImage(fileUpload, id).subscribe();
                    });
                })
            )
            .subscribe();
        return uploadPercentage(uploadTask);
    }

    pushDocumentToStorage(fileUpload: FileUploadProfile, idUser: number, idDocument: number | undefined): Observable<any> {
        const filePath = `agents-documents-required/${this.sessionDataProfile.idClient}/${idUser}/${idDocument}/${fileUpload.file.name}`;
        const uploadTask = uploadFile(filePath, fileUpload.file);

        uploadSnapshots(uploadTask)
            .pipe(
                finalize(() => {
                    downloadUrl(filePath).subscribe((downloadURL) => {
                        fileUpload.path = filePath;
                        fileUpload.url = downloadURL;
                        fileUpload.name = fileUpload.file.name;
                        this.updateDocument(fileUpload, idDocument).subscribe();
                    });
                })
            )
            .subscribe();

        return uploadSnapshots(uploadTask);
    }

    pushOnlyDocumentToStorage(fileUpload: FileUploadProfile, idUser: number, idDocument: number | undefined): Observable<any> {
        const filePath = `agents-documents-required/${this.sessionDataProfile.idClient}/${idUser}/${idDocument}/${fileUpload.file.name}`;
        return uploadThenUrl(filePath, fileUpload.file);
    }

    updateGeneral(user: AgentModel): Observable<ResponseModel<AgentModel>> {
        this.sessionData.full_name = `${user.first_name} ${user.last_name}`;
        this.sessionData.email = `${user.email}`;
        localStorage.setItem("dataAgent", JSON.stringify(this.sessionData));

        if (user.id == this.sessionDataProfile.idUser) {
            this.sessionDataProfile.fullName = `${user.first_name} ${user.last_name}`;
            this.sessionDataProfile.email = `${user.email}`;
            localStorage.setItem("data", JSON.stringify(this.sessionDataProfile));
        }

        this.updateHeaderAgentData(this.sessionData, this.sessionDataProfile);
        return this._apiService.put("/user/agent/general", user);
    }

    updateGeneralPassword(user: AgentModel): Observable<ResponseModel<AgentModel>> {
        return this._apiService.put("/user/agent/password", user);
    }

    updateLocation(user: AgentModel): Observable<ResponseModel<AgentModel>> {
        return this._apiService.put("/user/agent/location", user);
    }

    updateBusiness(user: AgentModel): Observable<ResponseModel<AgentModel>> {
        return this._apiService.put("/user/agent/business", user);
    }

    updatePayroll(user: AgentModel): Observable<ResponseModel<AgentModel>> {
        return this._apiService.put("/user/agent/payroll", user);
    }

    updateAgency(user: AgentModel): Observable<ResponseModel<AgentModel>> {
        return this._apiService.put("/user/agent/agency", user);
    }

    updateImage(file: FileUploadProfile, id: number): Observable<ResponseModel<AgentModel>> {
        this.sessionData.image_url = `${file.url}`;

        const params = {
            id,
            image_url: file.url,
            image_path: file.path,
        };
        if (id == this.sessionDataProfile.idUser) {
            this.sessionDataProfile.image_url = `${file.url}`;
            localStorage.setItem("data", JSON.stringify(this.sessionDataProfile));
        }
        localStorage.setItem("dataAgent", JSON.stringify(this.sessionData));
        this.updateHeaderAgentData(this.sessionData, this.sessionDataProfile);
        return this._apiService.put("/user/agent/setimage", params);
    }

    updateDocument(file: FileUploadProfile, id: number | undefined): Observable<ResponseModel<DocumentAgentModel>> {
        const params = {
            id: id,
            url_document: file.url,
            url_path: file.path,
        };
        return this._apiService.put("/documentagent/urldocument", params);
    }

    deleteFile(path: string): Observable<ResponseModel<AgentModel>> {
        this.sessionData.image_url = "";
        this.sessionDataProfile.image_url = "";
        const params = {
            image_url: null,
            image_path: null,
        };
        this.deleteFileStorage(path);
        this.updateHeaderAgentData(this.sessionData, this.sessionDataProfile); //para actualizar en tiempo real los cambios en el header del profile
        return this._apiService.put("/user/agent/setimage", params);
    }

    deleteDocument(documentAgent: DocumentAgentModel): Observable<ResponseModel<DocumentAgentModel>> {
        const params = {
            id: documentAgent.id,
            url_document: null,
            url_path: null,
        };
        this.deleteDocumentStorage(documentAgent.url_path!);
        return this._apiService.put("/documentagent/deletedocument", params);
    }

    selectCodeCountry(): Observable<ResponseModel<CountrySelect[]>> {
        return this._apiService.get("/countrylist");
    }

    selectCodeCountryOne(code: number): Observable<ResponseModel<CountrySelect>> {
        return this._apiService.get(`/country`, { id: code });
    }

    selectAlphaCountryOne(code: string): Observable<ResponseModel<CountrySelect>> {
        return this._apiService.get(`/country`, { alphacode: code });
    }

    selectLanguages(): Observable<ResponseModel<Languages[]>> {
        return this._apiService.get("/languages/list");
    }

    selectStates(): Observable<ResponseModel<StatesSelect[]>> {
        return this._apiService.get("/states/list");
    }

    getResidenceLicense(user: number): Observable<ResponseModel<NoResidenceLicenseModel[]>> {
        let data = { id_user: user };
        return this._apiService.get("/residentlicense/user", data);
    }

    getResidenceLicensePaginator(
        params: ParametersModel,
        user: number
    ): Observable<ResponseModel<pageableModel<NoResidenceLicenseModel[]>>> {
        let id_user = typeof user === "number" ? user : "";
        return this._apiService.get(`/residentlicense/paginator`, {
            start: params.start,
            length: params.length,
            order: params.order,
            sort: params.sort,
            search: typeof params.search === "string" ? params.search : "",
            id_user: id_user,
        });
    }

    getOneResidenceLicense(id: number): Observable<ResponseModel<NoResidenceLicenseModel>> {
        let data = { id: id };
        return this._apiService.get(`/residentlicense`, data);
    }

    addResidenceLicense(noResidenceLicense: NoResidenceLicenseModel): Observable<ResponseModel<NoResidenceLicenseModel>> {
        return this._apiService.post("/residentlicense", noResidenceLicense);
    }

    updateResidenceLicense(noResidenceLicense: NoResidenceLicenseModel): Observable<ResponseModel<NoResidenceLicenseModel>> {
        return this._apiService.put("/residentlicense", noResidenceLicense);
    }

    deleteResidenceLicense(noResidenceLicense: NoResidenceLicenseModel): Observable<ResponseModel<NoResidenceLicenseModel>> {
        let data = { id: noResidenceLicense.id };
        return this._apiService.deleteByParams("/residentlicense", data);
        // return this._apiService.delete('/residentlicense', noResidenceLicense);
    }

    changeStatusLife(id: number, status: number): Observable<ResponseModel<NoResidenceLicenseModel>> {
        let data = { id: id, flag_Life: status };
        return this._apiService.put("/residentlicense/change/life", data);
    }

    changeStatusHealth(id: number, status: number): Observable<ResponseModel<NoResidenceLicenseModel>> {
        let data = { id: id, flag_Health: status };
        return this._apiService.put("/residentlicense/change/health", data);
    }

    changeStatusApproved(id: number, status: number): Observable<ResponseModel<NoResidenceLicenseModel>> {
        let data = { id: id, flag_approved: status };
        return this._apiService.put("/residentlicense/change/approved", data);
    }

    //Documents Agent
    getDocumentsType(): Observable<ResponseModel<DocumentTypeModel[]>> {
        return this._apiService.get("/documentstype/list");
    }

    getAgentDocumentList(id_agent: number): Observable<ResponseModel<DocumentAgentModel[]>> {
        let data = { id: id_agent };
        return this._apiService.get("/documentagent/list", data);
    }

    getOneAgentDocument(id_agent: number): Observable<ResponseModel<DocumentAgentModel>> {
        let data = { id: id_agent };
        return this._apiService.get("/documentagent", data);
    }

    addAgentDocument(agentDocument: DocumentAgentModel): Observable<ResponseModel<DocumentAgentModel>> {
        return this._apiService.post("/documentagent", agentDocument);
    }

    private deleteFileStorage(path: string): void {
        let name = path.split("/");
        let newPath = `/${name[2]}/${name[3]}/${name[4]}`;
        deleteObjectAt(`${this.basePath}${newPath}`).subscribe();
    }

    private deleteDocumentStorage(path: string): void {
        deleteObjectAt(`/${path}`).subscribe();
    }
}
